import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight, User, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createPageUrl } from '@/utils';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';
import NotificationBell from '@/components/NotificationBell';
import ThemeToggle from '@/components/ThemeToggle';
import { BTN_INVERT, BTN_QUIET } from '@/components/bento';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* ── Brand logos (inline SVG, currentColor) ─────────────────────────────────── */

const social = {
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 01-2.88 0 1.44 1.44 0 012.88 0z"/></svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.028C.533 9.046-.32 13.58.099 18.058a.082.082 0 00.031.056 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
  ),
};

/* ── Nav structure ──────────────────────────────────────────────────────────── */

const NAV = [
  {
    label: 'Resources',
    items: [
      { label: 'About', page: 'About' },
      { label: 'Services', page: 'Services' },
      { label: 'Custom Builds', page: 'CustomBuilds' },
      { label: 'Portfolio', page: 'Portfolio' },
      { label: 'FAQs', page: 'FAQs' },
      { label: 'Team', page: 'Team' },
      { label: 'Contact', page: 'Contact' },
    ],
  },
  {
    label: 'Community',
    items: [
      { label: 'Facebook', href: 'https://facebook.com/builderslabofficial', icon: social.facebook },
      { label: 'Instagram', href: 'https://instagram.com/builderslabofficial', icon: social.instagram },
      { label: 'WhatsApp', href: 'https://chat.whatsapp.com/J3dXmpdAVlKAmGfFh9j1BG', icon: social.whatsapp },
      { label: 'X', href: 'https://x.com/builderslabofficial', icon: social.x },
      { label: 'LinkedIn', href: 'https://linkedin.com/company/builderslab', icon: social.linkedin },
      { label: 'Discord', href: 'https://discord.gg/builderslabofficial', icon: social.discord },
    ],
  },
  { label: 'Marketplace', page: 'Marketplace' },
  { label: 'Pricing', page: 'Pricing' },
  { label: 'Academy', page: 'Academy' },
  {
    label: 'Legal',
    items: [
      { label: 'Privacy Policy', page: 'PrivacyPolicy' },
      { label: 'Terms of Use', page: 'TermsOfUse' },
      { label: 'Refund Policy', page: 'RefundPolicy' },
      { label: 'Affiliate T&C', page: 'AffiliateTnC' },
      { label: 'Acceptable Use', page: 'AcceptableUsePolicy' },
      { label: 'Data & Compliance', page: 'DataCompliance' },
    ],
  },
];

/* Look groups up by label — the mobile menu used to index NAV positionally,
   which broke the moment an entry was inserted above one of them. */
const navGroup = (label) => NAV.find((entry) => entry.label === label)?.items ?? [];

function DesktopDropdown({ group }) {
  return (
    <div className="relative group/nav">
      <button className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[15px] font-medium text-zinc-400 group-hover/nav:text-white group-hover/nav:bg-zinc-900 group-focus-within/nav:text-white transition-colors">
        {group.label}
        <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover/nav:rotate-180" />
      </button>
      {/* Hover bridge + panel. focus-within as well as hover, so the panel
          opens for someone tabbing through the bar, not only for a mouse. */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 group-hover/nav:visible group-hover/nav:opacity-100 group-focus-within/nav:visible group-focus-within/nav:opacity-100 transition-opacity duration-150 z-50">
        <div className="w-56 bg-zinc-900 border border-zinc-800 rounded-2xl p-2 shadow-xl shadow-black/20">
          {group.items.map((item) =>
            item.href ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                <span className="text-zinc-500">{item.icon}</span>
                {item.label}
              </a>
            ) : (
              <Link
                key={item.label}
                to={createPageUrl(item.page)}
                className="block px-3 py-2.5 rounded-xl text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                {item.label}
              </Link>
            )
          )}
        </div>
      </div>
    </div>
  );
}

const mobileLink = 'block text-base font-medium text-zinc-400 hover:text-white transition-colors py-2.5 px-4 rounded-xl hover:bg-zinc-800';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  // Which form the modal opens on: "Start free" means a new account.
  const [authTab, setAuthTab] = useState('signin');
  const { user, profile, signOut } = useAuth();
  // The photo set in Settings, once there is one — the session user carries it
  // after a change, the shared profile from the moment the account loads.
  const avatarUrl = profile?.avatar_url || user?.user_metadata?.avatar_url || null;
  const displayName = profile?.full_name || user?.user_metadata?.full_name || '';

  // Close auth modal as soon as the user is authenticated
  useEffect(() => {
    if (user && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
  };

  const openAuth = (tab) => {
    setAuthTab(tab);
    setIsMobileMenuOpen(false);
    setIsAuthModalOpen(true);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
          isScrolled || isMobileMenuOpen
            // Blurred as well as tinted: at 90% alone, the page's text read
            // through the bar while scrolling and fought the logo and Login.
            ? 'bg-zinc-950/85 backdrop-blur-md border-zinc-800'
            : 'bg-transparent border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto h-16 px-4 sm:px-6 flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to={createPageUrl('Home')} className="flex items-center gap-2.5 group shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img
              src="/images/builderslab-logo.png"
              alt="BuildersLab Logo"
              className="h-7 w-auto object-contain group-hover:scale-105 transition-transform"
            />
            <span className="text-white text-lg font-bold tracking-tight">BuildersLab</span>
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main" className="hidden lg:flex items-center gap-0.5">
            {NAV.map((entry) => {
              if (entry.items) return <DesktopDropdown key={entry.label} group={entry} />;
              return (
                <Link
                  key={entry.label}
                  to={createPageUrl(entry.page)}
                  className="px-3.5 py-2 rounded-full text-[15px] font-medium text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
                >
                  {entry.label}
                </Link>
              );
            })}
          </nav>

          {/* Action Area */}
          <div className="flex items-center gap-2 shrink-0">
            <ThemeToggle />
            {!user ? (
              <>
                <button
                  type="button"
                  onClick={() => openAuth('signin')}
                  className="hidden sm:inline-flex h-10 items-center px-3.5 rounded-full text-[15px] font-medium text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                >
                  Sign in
                </button>
                <Button
                  className={`${BTN_INVERT} font-semibold rounded-full h-10 px-4 sm:px-5 text-sm sm:text-[15px] cursor-pointer`}
                  onClick={() => openAuth('signup')}
                >
                  Start free
                </Button>
              </>
            ) : (
              <>
                <NotificationBell user={user} />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" aria-label="Account menu" className="relative h-10 w-10 rounded-full cursor-pointer hover:bg-zinc-800 border border-zinc-800 p-0">
                    <Avatar className="h-8 w-8">
                      {avatarUrl && <AvatarImage src={avatarUrl} alt="" className="object-cover" />}
                      <AvatarFallback className="bg-orange-500 font-semibold text-ink uppercase">
                        {(displayName || user.email || '').charAt(0) || <User className="w-4 h-4" />}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800 text-zinc-200 rounded-2xl p-1.5" align="end" forceMount>
                  {(displayName || user.email) && (
                    <div className="px-2.5 py-2 border-b border-zinc-800 mb-1">
                      {displayName && <p className="text-xs font-medium text-white truncate">{displayName}</p>}
                      <p className="text-[11px] text-zinc-500 truncate">{user.email}</p>
                    </div>
                  )}
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl hover:bg-zinc-800 hover:text-white focus:bg-zinc-800 focus:text-white">
                    <Link to="/dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl hover:bg-zinc-800 hover:text-white focus:bg-zinc-800 focus:text-white">
                    <Link to="/dashboard?section=earn">Earn — referrals & sales</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl hover:bg-zinc-800 hover:text-white focus:bg-zinc-800 focus:text-white">
                    <Link to="/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer rounded-xl text-red-500 hover:bg-zinc-800 hover:text-red-400 focus:bg-zinc-800 focus:text-red-400">
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden h-10 w-10 grid place-items-center rounded-full text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              // An icon with no name was announced as just "button".
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-[4.5rem] left-3 right-3 bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl shadow-black/30 max-h-[75vh] overflow-y-auto">
            <div className="px-4 py-5 flex flex-col gap-1">

              <p className="px-4 pt-1 pb-2 text-[11px] font-bold uppercase tracking-widest text-orange-400">Resources</p>
              {navGroup('Resources').map((item) => (
                <Link
                  key={item.label}
                  to={createPageUrl(item.page)}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={mobileLink}
                >
                  {item.label}
                </Link>
              ))}

              <div className="my-3 border-t border-zinc-800" />
              <Link
                to={createPageUrl('Marketplace')}
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLink}
              >
                Marketplace
              </Link>
              <Link
                to={createPageUrl('Pricing')}
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLink}
              >
                Pricing
              </Link>
              <Link
                to={createPageUrl('Academy')}
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLink}
              >
                Academy
              </Link>
              <Link
                to={createPageUrl('AIBuilder')}
                onClick={() => setIsMobileMenuOpen(false)}
                className={mobileLink}
              >
                AI Builder
              </Link>

              <div className="my-3 border-t border-zinc-800" />
              <p className="px-4 pb-2 text-[11px] font-bold uppercase tracking-widest text-orange-400">Legal</p>
              {navGroup('Legal').map((item) => (
                <Link
                  key={item.label}
                  to={createPageUrl(item.page)}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-sm font-medium text-zinc-500 hover:text-white transition-colors py-2 px-4 rounded-xl hover:bg-zinc-800"
                >
                  {item.label}
                </Link>
              ))}

              <div className="my-3 border-t border-zinc-800" />
              <p className="px-4 pb-3 text-[11px] font-bold uppercase tracking-widest text-orange-400">Community</p>
              <div className="flex items-center gap-2 px-4 pb-2 flex-wrap">
                {navGroup('Community').map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
                  >
                    {item.icon}
                  </a>
                ))}
              </div>

              <div className="pt-4 mt-2 border-t border-zinc-800">
                {!user ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="lg"
                      variant="ghost"
                      className={`${BTN_QUIET} w-full font-semibold rounded-full`}
                      onClick={() => openAuth('signin')}
                    >
                      Sign in
                    </Button>
                    <Button
                      size="lg"
                      className={`${BTN_INVERT} w-full font-semibold rounded-full`}
                      onClick={() => openAuth('signup')}
                    >
                      Start free
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start rounded-xl bg-transparent border-zinc-800 text-zinc-200 hover:bg-zinc-800 hover:text-white">Dashboard</Button>
                    </Link>
                    <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start rounded-xl bg-transparent border-zinc-800 text-zinc-200 hover:bg-zinc-800 hover:text-white">Settings</Button>
                    </Link>
                    <Button variant="outline" onClick={handleSignOut} className="w-full justify-start rounded-xl bg-transparent border-zinc-800 text-red-500 hover:bg-zinc-800 hover:text-red-400">
                      Sign Out
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <AuthModal isOpen={isAuthModalOpen} onClose={setIsAuthModalOpen} initialTab={authTab} />
    </>
  );
}
